import {
    CalendarIcon,
    CurrencyIcon,
    EllipseFilledIcon,
    EllipseHalfIcon,
    LocationIcon,
    WorldIcon,
} from '@bi-digital/icons';
import { Link, useMediaQuery } from '@bi-digital/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { Programme } from '../../../types';
import styles from './ProgrammeInformation.module.css';
import classNames from 'classnames';

export interface IntroContainerProps {
    programme: Programme;
}

export default function ProgrammeInformation({ programme }: IntroContainerProps) {
    const isLargeScreen = useMediaQuery('(min-width: 1128px)');
    const isSmallScreen = useMediaQuery('(max-width: 840px)');

    const { t, i18n } = useTranslation();

    const campuses = programme.campuses
        .map((campus) => t(`cart:campus.${campus}`, { defaultValue: campus }))
        .join(', ');
    const hasPriceAndTuitionFeeBlock = programme.hasPriceAndTuitionFeeBlock;
    const tuitionFeeUrl = i18n.language === 'en' ? '#tuition-fee' : '#studieavgift';
    const iconSize = isLargeScreen ? 'large' : 'medium';

    const items: ListItemProps[] = [
        programme.loads.length > 0 && {
            icon: programme.loads.every((load) => load === 'FullTime') ? (
                <EllipseFilledIcon size={iconSize} aria-hidden='true' />
            ) : (
                <EllipseHalfIcon size={iconSize} aria-hidden='true' />
            ),
            label: t('cart:loadType'),
            value: programme.loads.map((load) => t(`cart:load.${load}`)).join(', '),
        },
        programme.terms.length > 0 && {
            icon: <CalendarIcon size={iconSize} aria-hidden='true' />,
            label: t('cart:term'),
            value: programme.terms
                .map((term) => t(`cart:${term.semester.toLowerCase()}`, { year: term.year }))
                .join(', '),
        },
        {
            icon: <WorldIcon size={iconSize} aria-hidden='true' />,
            label: t('cart:languageLabel'),
            value: t(`cart:language.${programme.teachingLanguage}`),
        },
        campuses && {
            icon: <LocationIcon size={iconSize} aria-hidden='true' />,
            label: t('cart:location'),
            value: campuses,
        },
        hasPriceAndTuitionFeeBlock && {
            icon: <CurrencyIcon size={iconSize} aria-hidden='true' />,
            label: t('cart:tuitionFee'),
            value: t('cart:seeTuitionFee'),
            href: tuitionFeeUrl,
        },
    ].filter(Boolean) as ListItemProps[];

    return (
        <section className={styles.programmeInformationContainer} aria-label={t('cart:AriaLabelProgrammeInformation')}>
            <div className={styles.programmeInformationList}>
                {items.map((item, index) => (
                    <Fragment key={index}>
                        <ListItem {...item} />
                        {index < items.length - 1 && !isSmallScreen && <span className={styles.divider} />}
                    </Fragment>
                ))}
            </div>
        </section>
    );
}

interface ListItemProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    href?: string;
}

const ListItem = ({ icon, label, value, href }: ListItemProps) => {
    const isSmallScreen = useMediaQuery('(max-width: 840px)');

    return (
        <div className={classNames(styles.programmeInformationListItem, href && styles[`listItemLink`])}>
            {icon}
            <div className={styles.programmeInformationTextContainer}>
                {!isSmallScreen && <p className={styles.programmeInformationLabel}>{label}</p>}
                {href ? (
                    <Link className={styles.programmeFeeLink} href={href}>
                        {value}
                    </Link>
                ) : (
                    <p>{value}</p>
                )}
            </div>
        </div>
    );
};
